import { Chip, Grid } from "@mui/material";
import { FC, Fragment, useEffect, useState } from "react";
import KeyboardBackspaceTwoToneIcon from "@mui/icons-material/KeyboardBackspaceTwoTone";
import clubAvatar from "../../assets/clubAvatar.png";
import { useNavigate, useParams } from "react-router";
import {
  getClubBasicInfo,
  getClubMembers,
  getClubProposalsOverview,
  getClubStakings,
  getClubTreasuryOverview,
  getClubWithdrawalOverview,
  toggleClubVisibility,
} from "../../api/clubs/club.api";
import {
  IClubBasicInfo,
  IClubMembers,
  IClubProposals,
  IClubStakings,
  IClubTreasuryOverview,
  IWithdrawalOverview,
} from "../../common/interfaces/club.interface";
import AppCard from "../../components/cards/AppCard";
import { getClubType, getTrimmedPublicKey } from "../../utils/helpers";
import classes from "./clubDetails.module.scss";
import ClubMembers from "../../components/ClubMembers/ClubMembers";
import ClubProposals from "../../components/ClubProposals/ClubProposals";
import ClubTreasuryOverview from "../../components/ClubTreasuryOverview/ClubTreasuryOverview";
import ClubStaking from "../../components/ClubStaking/ClubStaking";
import ClubWithdrawal from "../../components/ClubWithdrawal/ClubWithdrawal";
import { EMPTY_STRING } from "../../common/constants/common.constants";
import { CLUBS } from "../../common/constants/routes.constants";

const ClubDetails: FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [loading, toggleLoading] = useState(true);
  const [clubMembers, setClubMembers] = useState<IClubMembers>();
  const [proposalsOverview, setProposalsOverview] = useState<IClubProposals>();
  const [treasuryOverview, setTreasuryOverview] =
    useState<IClubTreasuryOverview>();
  const [clubBasicInfo, setClubBasicInfo] = useState<IClubBasicInfo>();
  const [clubAddress, setClubAddress] = useState<string>();
  const [clubStakings, setClubStakings] = useState<IClubStakings>();
  const [withdrawalOverview, setWithdrawalOverview] =
    useState<IWithdrawalOverview>();

  useEffect(() => {
    void setupClubDetails();
  }, []);

  const setupClubDetails = async () => {
    try {
      toggleLoading(true);
      const { clubAddress } = params;
      if (!clubAddress) {
        throw new Error("Wrong club address");
      }
      setClubMembers(await getClubMembers(clubAddress));
      setProposalsOverview(await getClubProposalsOverview(clubAddress));
      setTreasuryOverview(await getClubTreasuryOverview(clubAddress));
      setClubBasicInfo(await getClubBasicInfo(clubAddress));
      setClubStakings(await getClubStakings(clubAddress));
      setWithdrawalOverview(await getClubWithdrawalOverview(clubAddress));
      setClubAddress(clubAddress);
      toggleLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const toggleHidden = async () => {
    try {
      if (clubBasicInfo && clubAddress) {
        const response = await toggleClubVisibility(
          clubAddress,
          !clubBasicInfo.isHidden
        );

        setClubBasicInfo({ ...clubBasicInfo, isHidden: response.isHidden });
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Fragment>
      {!loading && (
        <div>
          <button
            className={classes.backToClubs}
            onClick={() => navigate(CLUBS)}
          >
            <KeyboardBackspaceTwoToneIcon fontSize={"small"} />
            <p>Back to clubs</p>
          </button>
          {clubBasicInfo && (
            <div className={classes.clubDetails}>
              <div className={classes.clubBasicInfo}>
                <img src={clubAvatar} />
                <div>
                  <h4>{clubBasicInfo.name}</h4>
                  <p>
                    Created by{" "}
                    <span>
                      {clubBasicInfo.owner !== EMPTY_STRING
                        ? clubBasicInfo.owner
                        : getTrimmedPublicKey(clubBasicInfo.ownersPubKey)}
                    </span>
                  </p>
                  <p>
                    Created at{" "}
                    {new Date(clubBasicInfo.createdAt).toDateString()}
                  </p>
                  <p>
                    {clubBasicInfo.isHidden === true
                      ? "Show club"
                      : "Hide club"}
                  </p>
                  <div className={classes.slider}>
                    <div
                      className={`${classes.option} ${
                        clubBasicInfo.isHidden ? classes.selected : ""
                      }`}
                      onClick={toggleHidden}
                    ></div>
                    <div
                      className={`${classes.option} ${
                        !clubBasicInfo.isHidden ? classes.selected : ""
                      }`}
                      onClick={toggleHidden}
                    ></div>
                  </div>
                  <div className={classes.clubTags}>
                    {clubBasicInfo.tags.map((item) => (
                      <Chip
                        label={item.value}
                        sx={{
                          backgroundColor: "transparent !important",
                          border: "1px solid white",
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <Grid
                container
                spacing={{ xs: 1, md: 2, sm: 2 }}
                columns={{ xs: 4, sm: 8, md: 8 }}
              >
                <Grid item xs={12} sm={12} md={4}>
                  <AppCard
                    label={"Governance type"}
                    value={getClubType(clubBasicInfo.governanceType)}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={4}>
                  <AppCard
                    label={"Application status"}
                    value={clubBasicInfo.applicationStatus}
                  />
                </Grid>
              </Grid>
            </div>
          )}
          {clubMembers && <ClubMembers clubMembers={clubMembers} />}
          {proposalsOverview && (
            <ClubProposals proposalsOverview={proposalsOverview} />
          )}
          {treasuryOverview && clubAddress && (
            <ClubTreasuryOverview
              treasuryOverview={treasuryOverview}
              clubAddress={clubAddress}
            />
          )}
          {clubStakings && <ClubStaking stakings={clubStakings} />}
          {withdrawalOverview && clubAddress && (
            <ClubWithdrawal
              withdrawalOverview={withdrawalOverview}
              clubAddress={clubAddress}
            />
          )}
        </div>
      )}
    </Fragment>
  );
};

export default ClubDetails;
